import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import socket from '../socket';
import Board from '../components/Board';
import TopBar from '../components/TopBar';
import { Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { createSeededDeck } from '../game/seededDeck';
import { calculateScore, computeAiMove } from '../game/aiEngine';
import { applyMoveToBoard, canPlaceOnStack, cloneBoard, getTileValue, sweepStacks } from '../game/boardEngine';
import { apiRequest } from '../lib/api';

const GameRoom = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, isGuest } = useAuth();
  const username = user?.username;

  const searchParams = new URLSearchParams(location.search);
  const gameMode = searchParams.get('mode') || 'realtime';
  const challengeId = searchParams.get('challengeId') || '';
  const challengeSeed = searchParams.get('seed') || '';

  const [players, setPlayers] = useState([]);
  const [columns, setColumns] = useState([[], [], [], []]);
  const [stacks, setStacks] = useState([[], [], [], []]);
  const [aiColumns, setAiColumns] = useState([[], [], [], []]);
  const [aiStacks, setAiStacks] = useState([[], [], [], []]);
  const [currentTile, setCurrentTile] = useState(null);
  const [remaining, setRemaining] = useState(52);
  const [startTime, setStartTime] = useState(null);
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || 'medium');
  const [seed, setSeed] = useState(challengeSeed || `${Date.now()}-${Math.random()}`);
  const [deck, setDeck] = useState([]);
  const [deckIndex, setDeckIndex] = useState(0);
  const [finalMessage, setFinalMessage] = useState('');
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [autoMoveEvent, setAutoMoveEvent] = useState(null);
  const [musicMuted, setMusicMuted] = useState(() => localStorage.getItem('numerica-music-muted') === '1');

  const columnsRef = useRef(columns);
  const stacksRef = useRef(stacks);
  const aiColumnsRef = useRef(aiColumns);
  const aiStacksRef = useRef(aiStacks);
  const currentTileRef = useRef(currentTile);
  const gameStartedRef = useRef(gameStarted);
  const audioContextRef = useRef(null);
  const backgroundMusicRef = useRef(null);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);
  useEffect(() => {
    stacksRef.current = stacks;
  }, [stacks]);
  useEffect(() => {
    aiColumnsRef.current = aiColumns;
  }, [aiColumns]);
  useEffect(() => {
    aiStacksRef.current = aiStacks;
  }, [aiStacks]);
  useEffect(() => {
    currentTileRef.current = currentTile;
  }, [currentTile]);
  useEffect(() => {
    gameStartedRef.current = gameStarted;
  }, [gameStarted]);

  const score = calculateScore(stacks);
  const aiScore = calculateScore(aiStacks);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (gameMode !== 'realtime') return undefined;

    socket.emit('joinRoom', { roomId, username });

    socket.on('roomPlayers', (list) => setPlayers(list));
    socket.on('tileDrawn', ({ value, remaining, startTime }) => {
      setCurrentTile(value);
      setRemaining(remaining);
      if (startTime) setStartTime(startTime);
    });
    socket.on('tilePlaced', ({ targetType, targetIndex, value }) => {
      if (targetType === 'column') {
        setColumns((prev) => prev.map((col, idx) => idx === targetIndex ? [...col, value] : col));
      } else {
        setStacks((prev) => prev.map((stk, idx) => idx === targetIndex ? [...stk, value] : stk));
      }
      setCurrentTile(null);
    });
    socket.on('noTiles', () => alert('No more tiles to draw!'));
    socket.on('invalidPlacement', ({ message }) => alert(message));

    return () => {
      socket.off('roomPlayers');
      socket.off('tileDrawn');
      socket.off('tilePlaced');
      socket.off('noTiles');
      socket.off('invalidPlacement');
      socket.emit('leaveRoom', { roomId });
    };
  }, [roomId, username, navigate, gameMode]);

  useEffect(() => {
    if (gameMode === 'realtime') return;
    const initSeed = challengeSeed || `${Date.now()}-${Math.random()}`;
    setSeed(initSeed);
    setDeck(createSeededDeck(initSeed));
    setDeckIndex(0);
    setColumns([[], [], [], []]);
    setStacks([[], [], [], []]);
    setAiColumns([[], [], [], []]);
    setAiStacks([[], [], [], []]);
    setCurrentTile(null);
    setRemaining(52);
    setStartTime(null);
    setFinalMessage('');
    setScoreSubmitted(false);
    setGameStarted(false);
    setAutoMoveEvent(null);
  }, [gameMode, challengeSeed]);

  function playTileSfx(kind = 'move', intensity = 1) {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = kind === 'draw' ? 'triangle' : 'square';
      const now = ctx.currentTime;
      const baseFreq = kind === 'auto' ? 720 : kind === 'draw' ? 430 : 560;
      osc.frequency.setValueAtTime(baseFreq + Math.min(intensity, 6) * 24, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.14);
    } catch (_err) {
      // no-op if browser audio is blocked
    }
  }

  function startBackgroundMusic() {
    try {
      if (musicMuted) return;
      if (!backgroundMusicRef.current) {
        backgroundMusicRef.current = new Audio('/legacy/sounds/Untitled%20(1).mp3');
        backgroundMusicRef.current.loop = true;
        backgroundMusicRef.current.volume = 0.18;
      }
      if (backgroundMusicRef.current.paused) {
        backgroundMusicRef.current.play().catch(() => {});
      }
    } catch (_err) {
      // no-op when browser blocks audio
    }
  }

  useEffect(() => {
    localStorage.setItem('numerica-music-muted', musicMuted ? '1' : '0');
    if (musicMuted && backgroundMusicRef.current) {
      backgroundMusicRef.current.pause();
      return;
    }
    if (!musicMuted) {
      startBackgroundMusic();
    }
    // startBackgroundMusic is intentionally excluded to keep toggle side effects stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicMuted]);

  useEffect(() => {
    return () => {
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.pause();
        backgroundMusicRef.current.currentTime = 0;
      }
    };
  }, []);

  function dealNextTile() {
    if (gameMode === 'realtime') return;
    if (currentTileRef.current !== null) return;
    if (!gameStartedRef.current) return;

    setDeckIndex((prev) => {
      if (prev >= deck.length) return prev;
      const nextTile = deck[prev];
      setCurrentTile(nextTile);
      setRemaining(deck.length - (prev + 1));
      playTileSfx('draw');
      return prev + 1;
    });
  }

  function advanceAfterMove() {
    if (gameMode === 'realtime') return;
    if (!gameStartedRef.current) return;
    setTimeout(() => {
      dealNextTile();
    }, 150);
  }

  function applyPlayerMove(tileValue, targetType, targetIndex, autoAdvance = true) {
    const nextColumns = cloneBoard(columnsRef.current);
    const nextStacks = cloneBoard(stacksRef.current);

    if (targetType === 'column') {
      nextColumns[targetIndex].push(tileValue);
    } else {
      if (!canPlaceOnStack(nextStacks[targetIndex], tileValue)) return false;
      nextStacks[targetIndex].push(tileValue);
    }

    const swept = sweepStacks(nextColumns, nextStacks);
    columnsRef.current = swept.columns;
    stacksRef.current = swept.stacks;
    setColumns(swept.columns);
    setStacks(swept.stacks);
    setCurrentTile(null);
    currentTileRef.current = null;
    playTileSfx('move');

    if (swept.movedCount > 0) {
      setAutoMoveEvent({
        id: `${Date.now()}-${Math.random()}`,
        count: swept.movedCount
      });
      playTileSfx('auto', swept.movedCount);
    }

    if (gameMode === 'solo-ai') {
      const aiMove = computeAiMove({
        columns: aiColumnsRef.current,
        stacks: aiStacksRef.current,
        tileValue: getTileValue(tileValue),
        difficulty
      });
      let aiNextColumns = cloneBoard(aiColumnsRef.current);
      let aiNextStacks = cloneBoard(aiStacksRef.current);

      if (aiMove.targetType === 'column') {
        aiNextColumns = applyMoveToBoard(aiColumnsRef.current, 'column', aiMove.targetIndex, getTileValue(tileValue));
      } else {
        const placed = applyMoveToBoard(aiStacksRef.current, 'stack', aiMove.targetIndex, getTileValue(tileValue));
        aiNextStacks = placed || aiNextStacks;
      }
      const aiSwept = sweepStacks(aiNextColumns, aiNextStacks);
      aiColumnsRef.current = aiSwept.columns;
      aiStacksRef.current = aiSwept.stacks;
      setAiColumns(aiSwept.columns);
      setAiStacks(aiSwept.stacks);
    }

    if (autoAdvance) {
      advanceAfterMove();
    }
    return true;
  }

  useEffect(() => {
    async function submitResults() {
      if (gameMode === 'realtime') return;
      if (!startTime || scoreSubmitted) return;
      const done = deckIndex >= deck.length && currentTile === null;
      if (!done) return;

      const timeSeconds = Math.floor((Date.now() - startTime) / 1000);
      setScoreSubmitted(true);

      try {
        await apiRequest('/scores', {
          method: 'POST',
          token,
          body: {
            score,
            timeSeconds,
            difficulty,
            mode: gameMode === 'challenge' ? 'challenge' : 'solo',
            seed,
            username
          }
        });
      } catch (_err) {
        // keep play flow uninterrupted when score API fails
      }

      if (gameMode === 'challenge' && challengeId && !isGuest) {
        try {
          const payload = await apiRequest(`/challenges/${challengeId}/submit`, {
            method: 'POST',
            token,
            body: { score, timeSeconds }
          });
          const winnerName = payload.challenge.winnerUsername || 'pending';
          setFinalMessage(`Challenge submitted. Winner: ${winnerName}.`);
          return;
        } catch (err) {
          setFinalMessage(`Challenge submission failed: ${err.message}`);
          return;
        }
      }

      if (gameMode === 'solo-ai') {
        if (score > aiScore) setFinalMessage(`You win ${score} to ${aiScore}.`);
        else if (score < aiScore) setFinalMessage(`Computer wins ${aiScore} to ${score}.`);
        else setFinalMessage(`Draw at ${score}.`);
      } else {
        setFinalMessage(`Run complete: ${score} points in ${timeSeconds}s.`);
      }
    }
    submitResults();
  }, [
    gameMode,
    startTime,
    scoreSubmitted,
    deckIndex,
    deck.length,
    currentTile,
    score,
    aiScore,
    token,
    difficulty,
    seed,
    username,
    challengeId,
    isGuest
  ]);

  const handleDrawTile = () => {
    startBackgroundMusic();
    if (gameMode === 'realtime') {
      socket.emit('drawTile', { roomId });
      return;
    }
    dealNextTile();
  };

  const handleStartGame = () => {
    if (gameMode === 'realtime') return;
    if (gameStartedRef.current) return;
    startBackgroundMusic();
    setGameStarted(true);
    gameStartedRef.current = true;
    if (!startTime) setStartTime(Date.now());
    dealNextTile();
  };

  const handlePlaceTile = (targetType, targetIndex) => {
    if (currentTile === null) return;
    startBackgroundMusic();
    if (gameMode === 'realtime') {
      socket.emit('placeTile', { roomId, targetType, targetIndex });
      return;
    }

    applyPlayerMove(currentTile, targetType, targetIndex, true);
  };

  useEffect(() => {
    if (gameMode === 'realtime') return;
    if (!gameStarted) return;
    if (getTileValue(currentTile) !== 1) return;

    const targetStackIndex = stacksRef.current.findIndex((stack) => canPlaceOnStack(stack, 1));
    if (targetStackIndex >= 0) {
      applyPlayerMove(currentTile, 'stack', targetStackIndex, true);
    }
    // applyPlayerMove is intentionally excluded to avoid re-trigger loops while auto-placing 1 tiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTile, gameMode, gameStarted]);

  const difficultyLabel = difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  const modeChip =
    gameMode === 'realtime'
      ? `Live · ${roomId}`
      : gameMode === 'challenge'
        ? `Challenge · ${difficultyLabel}`
        : `vs Computer · ${difficultyLabel}`;

  return (
    <div className="page-shell">
      <TopBar chip={modeChip}>
        <button
          type="button"
          className="nav-link-btn"
          onClick={() => setMusicMuted((prev) => !prev)}
          aria-pressed={!musicMuted}
          title={musicMuted ? 'Turn music on' : 'Turn music off'}
        >
          {musicMuted ? '♪ Music off' : '♪ Music on'}
        </button>
        <button type="button" className="nav-link-btn" onClick={() => navigate('/rules')}>
          Rules
        </button>
        <button type="button" className="nav-link-btn" onClick={() => navigate('/history')}>
          History
        </button>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/lobby')}>
          Leave table
        </Button>
      </TopBar>

      {gameMode === 'realtime' && players.length > 0 && (
        <div className="players-strip mb-3" aria-label="Players at this table">
          {players.map((p, idx) => (
            <span key={idx} className="player-pill">
              {p}
            </span>
          ))}
        </div>
      )}

      {finalMessage && (
        <div className="surface-panel result-banner mb-3" role="status">
          {finalMessage}
        </div>
      )}

      <Board
        seed={seed}
        columns={columns}
        stacks={stacks}
        currentTile={currentTile}
        remaining={remaining}
        startTime={startTime}
        onDrawTile={handleDrawTile}
        onStartGame={handleStartGame}
        onPlaceTile={handlePlaceTile}
        difficulty={difficulty}
        onDifficultyChange={setDifficulty}
        aiScore={aiScore}
        modeLabel={gameMode}
        autoDealMode={gameMode !== 'realtime'}
        gameStarted={gameMode === 'realtime' ? true : gameStarted}
        autoMoveEvent={autoMoveEvent}
      />
    </div>
  );
};

export default GameRoom;